import React, { useState } from "react";
import { motion } from "framer-motion";
import { EyeOff, Lock, GraduationCap, MapPin } from "lucide-react";
import Modal, { Ext } from "./Modal";
import FounderCard from "./FounderCard";

const values = [
  {
    icon: EyeOff,
    title: "Discreción",
    text: "Tratamos cada operación con la máxima reserva. Sabemos que la información fiscal es estratégica.",
    modal: (
      <>
        <p>Tratamos cada operación con la máxima reserva. La información fiscal y comercial de nuestros clientes es estratégicamente sensible: volúmenes, proveedores, precios de adquisición y destinos de distribución.</p>
        <h4>Protocolo de discreción</h4>
        <ul>
          <li>No revelamos información de operaciones a terceros sin autorización expresa</li>
          <li>Datos de cada cliente gestionados en entornos separados y seguros</li>
          <li>Personal bajo acuerdos de confidencialidad internos</li>
          <li>Nunca usamos datos de operaciones de clientes con fines comerciales propios</li>
        </ul>
      </>
    ),
  },
  {
    icon: Lock,
    title: "Confidencialidad",
    text: "Acuerdos de confidencialidad por defecto y procesos blindados para proteger tu actividad.",
    modal: (
      <>
        <p>Todos los clientes pueden solicitar la firma de un NDA específico. Nuestro marco de protección de datos cumple íntegramente con:</p>
        <ul>
          <li><Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32016R0679">Reglamento (UE) 2016/679 (RGPD)</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-2018-16673">Ley Orgánica 3/2018 (LOPDGDD)</Ext></li>
          <li>Inscripción en el Registro de Actividades de Tratamiento (art. 30 RGPD)</li>
        </ul>
      </>
    ),
  },
  {
    icon: GraduationCap,
    title: "Especialización",
    text: "Equipo experto en Impuestos Especiales, RETEF, CAE, garantías y régimen suspensivo.",
    modal: (
      <>
        <p>FiscalBond se centra exclusivamente en impuestos especiales y régimen suspensivo.</p>
        <h4>Áreas de conocimiento</h4>
        <ul>
          <li>Impuesto sobre Alcohol y Bebidas Derivadas (Ley 38/1992, arts. 74-102)</li>
          <li>Impuesto sobre Hidrocarburos (Ley 38/1992, arts. 46-73)</li>
          <li>RETEF — RD 249/2023</li>
          <li>CAE — Código de Actividad y Establecimiento (art. 40 RD 1165/1995)</li>
          <li>Garantías ante la AEAT para operadores</li>
          <li>Declaraciones periódicas: modelos 561, 566, 570, 580</li>
          <li>Sistema EMCS y documentos e-AD para circulación intracomunitaria</li>
          <li>INCOTERMS 2020 y operaciones logísticas internacionales</li>
        </ul>
      </>
    ),
  },
  {
    icon: MapPin,
    title: "Proximidad",
    text: "Interlocución directa, sin departamentos opacos. Hablamos tu idioma y operativa.",
    modal: (
      <>
        <p>Interlocución directa con quien conoce tu operación de principio a fin.</p>
        <ul>
          <li>Interlocutor único para toda la operación</li>
          <li>Respuesta en menos de 24 horas hábiles</li>
          <li>Comunicación por email, teléfono o videollamada</li>
          <li>Sin burocracia ni derivaciones internas</li>
          <li>Disponibilidad para reuniones presenciales</li>
        </ul>
        <h4>Contacto directo</h4>
        <p>📞 926 032 626 / 633 089 026<br />✉️ info@fiscalbond.es</p>
      </>
    ),
  },
];

export const About = () => {
  const [open, setOpen] = useState(null);
  return (
    <section
      id="nosotros"
      data-testid="about-section"
      className="relative bg-[#F5F7FA] py-24 md:py-32 text-[#1A1A2E]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold">
              Sobre nosotros
            </span>
            <h2
              className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
              style={{ fontFamily: "Poppins" }}
            >
              Tu socio fiscal en{" "}
              <span className="text-[#0066FF]">impuestos especiales</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed">
              FiscalBond nace para ofrecer soluciones especializadas en
              impuestos especiales y régimen suspensivo. Somos{" "}
              <strong>titulares del RETEF concedido por la AEAT</strong> y
              actuamos como extractores autorizados por cuenta de terceros,
              asumiendo toda la gestión fiscal, documental y operativa.
            </p>
            <blockquote className="mt-8 border-l-4 border-[#FF6B00] pl-5 italic text-slate-800">
              «Nuestra posición en la cadena fiscal nos convierte casi en parte
              de la empresa de nuestros clientes.»
            </blockquote>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
            className="lg:col-span-7 grid sm:grid-cols-2 gap-4"
          >
            {values.map(({ icon: Icon, title, text }, i) => (
              <motion.button
                key={title}
                type="button"
                onClick={() => setOpen(i)}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.5 }}
                data-testid={`value-card-${title.toLowerCase()}`}
                className="group text-left bg-white rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0066FF]/10 hover:border-[#0066FF] cursor-pointer transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#1E3A5F] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628]" style={{ fontFamily: "Poppins" }}>
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{text}</p>
                <span className="mt-3 inline-block text-xs font-semibold text-[#0066FF]">Saber más →</span>
              </motion.button>
            ))}
          </motion.div>
        </div>

        <FounderCard />
      </div>

      {open !== null && (
        <Modal
          open={open !== null}
          onClose={() => setOpen(null)}
          title={values[open].title}
          eyebrow="Nuestros valores"
        >
          {values[open].modal}
        </Modal>
      )}
    </section>
  );
};

export default About;
